import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRouter } from 'next/router'
import { validateUrlSourceType } from '../utils/sourceTypes'
import { GetFromQuery, IsExact } from '../types/core'
import { sourceTypeSelected } from '../state/createCitation/actions'
import { styleSelected } from '../state/currentProject/actions'
import { createCitationSourceTypeSelector } from '../state/createCitation/selectors'
import {
  currentStyleSelector,
  styleGateFlagSelector,
} from '../state/currentProject/selectors'
import usePrevious from './usePrevious'

interface useUrlStateChangeProps {
  getSourceFromQuery?: GetFromQuery
  isSourceExact?: IsExact
  getStyleFromQuery?: GetFromQuery
  isStyleExact?: IsExact
  getActualStyleId?: (style: string) => string
}
const useUrlStateChange = ({
  getSourceFromQuery = (_, sourceQuery) => sourceQuery,
  isSourceExact = () => true,
  getStyleFromQuery = (_, styleQuery) => styleQuery,
  isStyleExact = () => true,
  getActualStyleId,
}: useUrlStateChangeProps = {}) => {
  const dispatch = useDispatch()
  const router = useRouter()
  const stateStyle = useSelector(currentStyleSelector)
  const stateSource = useSelector(createCitationSourceTypeSelector)
  const isStyleGateFlagEnabled = useSelector(styleGateFlagSelector)

  const { route, query } = router

  const { style: styleQuery, source: sourceQuery } = query as {
    style: string
    source: string
  }

  const urlSource = getSourceFromQuery(router, sourceQuery)
  const prevUrlSource = usePrevious(urlSource)
  const sourceIsExact = isSourceExact(route, sourceQuery)
  const isValidSource = urlSource ? validateUrlSourceType(urlSource, sourceIsExact) : true

  const shouldUpdateSourceType =
    urlSource && isValidSource && urlSource !== prevUrlSource && urlSource !== stateSource

  const urlStyle = getActualStyleId
    ? getActualStyleId(getStyleFromQuery(router, styleQuery, false))
    : getStyleFromQuery(router, styleQuery)
  const prevUrlStyle = usePrevious(urlStyle)
  const styleIsExact = isStyleExact(route, styleQuery)

  const shouldUpdateStyle =
    urlStyle && styleIsExact && urlStyle !== prevUrlStyle && urlStyle !== stateStyle

  useEffect(() => {
    if (shouldUpdateStyle || (urlStyle && isStyleGateFlagEnabled))
      dispatch(styleSelected(urlStyle, true, false, isStyleGateFlagEnabled))
    if (shouldUpdateSourceType) dispatch(sourceTypeSelected(urlSource))
  }, [
    shouldUpdateStyle,
    urlStyle,
    shouldUpdateSourceType,
    urlSource,
    isStyleGateFlagEnabled,
  ])

  return {
    urlIsValid: isValidSource && styleIsExact,
  }
}

export default useUrlStateChange
